
import React, { useEffect, useState } from 'react';
import PomodoroTimer from '@/components/PomodoroTimer';
import Footer from '@/components/Footer';
import FullscreenControl from '@/components/FullscreenControl';
import UnsplashAttribution from '@/components/UnsplashAttribution';
import BackgroundDebug from '@/components/BackgroundDebug';
import BackgroundRefreshButton from '@/components/BackgroundRefreshButton';
import { usePreferredBackground } from '@/hooks/usePreferredBackground';
import GradientBackground from "@/components/GradientBackground";

// Add type guard for TimeAwareBackgroundInfo
function isTimeAwareBackgroundInfo(obj: any): obj is { timeSlot: string; animated?: boolean } {
  return obj && typeof obj === "object" && "timeSlot" in obj;
}

interface SharedLayoutProps {
  children: React.ReactNode;
}

const SharedLayout: React.FC<SharedLayoutProps> = ({ children }) => {
  const { backgroundInfo, isLoading } = usePreferredBackground();
  const [showDebug, setShowDebug] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [backgroundStyle, setBackgroundStyle] = useState<React.CSSProperties>({
    background: 'linear-gradient(to bottom right, #1a202c, #2d3748)'
  });

  // Show debug info in development or when explicitly enabled
  useEffect(() => {
    const isDev = import.meta.env.DEV;
    const debugParam = new URLSearchParams(window.location.search).has('debug');
    setShowDebug(isDev || debugParam);
  }, []);

  // Render webgl gradient background
  const showWebGLGradient =
    isTimeAwareBackgroundInfo(backgroundInfo) &&
    backgroundInfo.animated &&
    backgroundInfo.url === "lovable-timeofday-webgl-gradient";

  useEffect(() => {
    if (
      backgroundInfo &&
      !isLoading &&
      !showWebGLGradient // only process gradient style if not using the special webgl
    ) {
      // Detect if we're using a linear-gradient
      if (
        typeof backgroundInfo.url === "string" &&
        backgroundInfo.url.startsWith('linear-gradient(')
      ) {
        setBackgroundStyle({
          background: backgroundInfo.url,
          transition: 'background 0.5s ease-in-out'
        });
      } else {
        setBackgroundStyle({
          backgroundImage: `url(${backgroundInfo.url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transition: 'background-image 0.5s ease-in-out'
        });
      }
    }
  }, [backgroundInfo, isLoading, showWebGLGradient, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    // Force a page reload to refresh the background
    window.location.reload();
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Animated gradient background under Pomodoro area */}
      {showWebGLGradient && isTimeAwareBackgroundInfo(backgroundInfo) && (
        <GradientBackground
          timeSlot={backgroundInfo.timeSlot as any}
        />
      )}
      <div 
        className="relative" 
        style={!showWebGLGradient ? backgroundStyle : {}} // Only use inline bg style if not WebGL
        aria-label="Pomodoro Timer with time-aware background image"
      >
        <PomodoroTimer />
        {backgroundInfo && (
          <UnsplashAttribution 
            photographerName={backgroundInfo.photographer}
            photographerUrl={backgroundInfo.photographerUrl}
            location={backgroundInfo.location}
          />
        )}
      </div>
      
      <div className="bg-background">
        <div className="container mx-auto px-4">
          {children}
        </div>
        <Footer />
      </div>
      <FullscreenControl />
      
      {/* Debug and refresh components */}
      <BackgroundDebug isVisible={showDebug} />
      <BackgroundRefreshButton 
        onRefresh={handleRefresh}
        isVisible={showDebug}
      />

      <style>
        {`
          .glass-panel {
            border-radius: 16px !important;
            backdrop-filter: blur(10px);
            background: rgba(0, 0, 0, 0.3);
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
            transition: all 0.3s ease;
          }
          .nav-bar img {
            text-shadow: 0 1px 3px rgba(0, 0, 0, 0.7);
            filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.7));
          }
        `}
      </style>
    </div>
  );
};

export default SharedLayout;
